import { BOGGLE_VOWEL_PARING_ALLOWED_VALUES } from '~/boggle/config/challengeModLetterConstants'
import type { BoggleChallengeBoardDistributionDefinition } from '~/boggle/config/boardDistributions/boardDistributionDefinition'
import type { BoggleResolvedChallengeBoardDistribution } from '~/boggle/config/challenges/modTypes'
import { pickWeightedEntry } from '~/boggle/lib/challenge/random'
import { formatResolvedBoardDistributionDisplayFromDefinition } from '~/boggle/config/boardDistributions/formatResolvedBoardDistributionDisplay'
import { VOWEL_PARING_VARIANTS, VOWEL_VALUES } from '~/boggle/lib/challenge/parameterVariants'

export const vowelParingBoardDistributionDefinition: BoggleChallengeBoardDistributionDefinition = {
  description: 'Only certain vowels can appear',
  formatResolvedDisplay: ({ parameters }) => {
    const allowedVowels = parameters.allowedVowels?.value as readonly string[] | undefined
    const pair = String(parameters.pair?.value ?? '')
    if (!allowedVowels || allowedVowels.length === 0 || pair.length === 0) {
      return {
        description: 'Only certain vowels can appear',
        name: 'Vowel Paring',
        shortLabel: 'Vowel pair',
      }
    }

    return {
      description: `${(allowedVowels ?? []).join(' and ')} are the only vowels`,
      name: 'Vowel Paring',
      shortLabel: `${pair} vowels`,
    }
  },
  id: 'vowel-paring',
  name: 'Vowel Paring',
  parameterDefinitions: {
    allowedVowels: {
      itemValues: [...VOWEL_VALUES],
      kind: 'string-list',
      max: 2,
      min: 2,
    },
    pair: {
      kind: 'enum',
      max: BOGGLE_VOWEL_PARING_ALLOWED_VALUES.length - 1,
      min: 0,
      values: BOGGLE_VOWEL_PARING_ALLOWED_VALUES,
    },
  },
  selectionWeight: 4,
  shortLabel: 'Vowel pair',
}

export function resolveVowelParingBoardDistribution(
  family: BoggleChallengeBoardDistributionDefinition,
  rngState: number,
): {
  mod: BoggleResolvedChallengeBoardDistribution
  rngState: number
} {
  const pickedPair = pickWeightedEntry(VOWEL_PARING_VARIANTS, rngState)
  if (pickedPair === null) {
    throw new Error(`No vowel-paring pairs available for ${family.id}`)
  }

  const resolvedAllowedVowels = [...pickedPair.entry.allowedVowels]
  const pair = resolvedAllowedVowels.join('/')
  const blockedVowels = VOWEL_VALUES.filter(
    letter => !resolvedAllowedVowels.includes(letter),
  )
  const parameters = {
    allowedVowels: {
      definition: family.parameterDefinitions.allowedVowels!,
      value: resolvedAllowedVowels,
    },
    pair: {
      definition: family.parameterDefinitions.pair!,
      value: pair,
    },
  }
  const display = formatResolvedBoardDistributionDisplayFromDefinition(family, parameters)

  return {
    mod: {
      description: display.description,
      effects: blockedVowels.map(letter => ({
        kind: 'letter-weights' as const,
        transform: {
          kind: 'remove-letter' as const,
          letter,
        },
      })),
      familyId: family.id,
      id: `vowel-paring-${pair.toLowerCase().replace('/', '-')}`,
      name: display.name,
      outcomes: [
        {
          kind: 'allowed-vowels' as const,
          letters: resolvedAllowedVowels,
        },
      ],
      parameters,
      shortLabel: display.shortLabel,
    },
    rngState: pickedPair.rngState,
  }
}
